/**
 * @file customers.api.js
 * @module CustomersAPI
 * @description HTTP API wrapper for customer-related endpoints (CRUD, search, options).
 * All methods return an Axios Promise (axiosInstance(token).get/post/put/patch/delete).
 */

import { axiosInstance } from "./axios"

export const CustomersAPI = {

    /**
     * Create a new customer.
     * @param {Object} customer - Customer payload.
     * @param {string} [type="complete"] - Creation type.
     * @param {string} [token=""] - Optional auth token.
     * @returns {Promise} Axios promise resolving to created customer.
     */
    createCustomer: (customer, type = "complete", token = "") => {
        return axiosInstance(token).post(`/customers?origin=internal&type=${type}`, customer)
    },

    /**
     * Retrieve customers list with pagination and optional query.
     * @param {number} [limit=15]
     * @param {number} [page=1]
     * @param {string} [query=""]
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to customers list.
     */
    getCustomers: (limit = 15, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/customers?limit=${limit}&page=${page}${query ? `&${query}` : ""}`)
    },

    /**
     * Get a single customer by id.
     * @param {string} customerId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to customer data.
     */
    getCustomerById: (customerId, token = "") => {
        return axiosInstance(token).get(`/customers/${customerId}`)
    },

    /**
     * Update a customer by id.
     * @param {string} customerId
     * @param {Object} customer - Updated customer payload.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to updated customer.
     */
    updateCustomerById: (customerId, customer, token = "") => {
        return axiosInstance(token).put(`/customers/${customerId}`, customer)
    },

    /**
     * Delete a customer by id.
     * @param {string} customerId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteCustomerById: (customerId, token = "") => {
        return axiosInstance(token).delete(`/customers/${customerId}`)
    },

    /**
     * Delete multiple customers by payload.
     * @param {Array|Object} customers
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyCustomersById: (customers, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-customers`, customers)
    },

    /**
     * Get customers options (lightweight list for selects).
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to options list.
     */
    customerOptions: (token = "") => {
        return axiosInstance(token).get(`/customers-options`)
    },

    /**
     * Send confirmation email to customer's account.
     * @param {string} customerId
     * @param {string} email
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendEmailToConfirmAccount: (customerId, email, token = "") => {
        return axiosInstance(token).post(`/customers/${customerId}/confirm-account`, { email: email })
    },

    /**
     * Send password recovery email to customer.
     * @param {string} customerId
     * @param {string} email
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendEmailToRecoverPassword: (customerId, email, token = "") => {
        return axiosInstance(token).post(`/customers/${customerId}/password-recovery`, { email: email })
    },

    /**
     * Register a customer as an agent under a specific agency.
     * @param {string} partnerId
     * @param {string} agencyId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to registration result.
     */
    registerAsAgent: (partnerId, agencyId, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/register-as-agent`, { agencyToAssign: agencyId })
    },

    /**
     * Approve a partner registration.
     * @param {string} agencyId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to approval result.
     */
    approvalRegister: (agencyId, token = "") => {
        return axiosInstance(token).put(`/partners/${agencyId}/approved`)
    },

    /**
     * Get partners options.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to options list.
     */
    getPartnersOptions: (token = "") => {
        return axiosInstance(token).get(`/partners-options`)
    },

    /**
     * Reject a partner registration.
     * @param {string} partnerId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to reject result.
     */
    rejectRegistration: (partnerId, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/reject-registration`)
    },

    /**
     * Changes the customer to a partner and assigns to a specific agency.
     * @param {string} customerId - ID of the customer.
     * @param {string} changeTo - Type to change to (e.g., 'agency'|'agent').
     * @param {string} agencyToAssign - ID of the agency to which the customer is assigned.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to the update result.
     */
    changeToPartner: (customerId, changeTo, agencyToAssign, token = "") => {
        return axiosInstance(token).put(`/customers/${customerId}/change-to-partner`, { changeTo: changeTo, agencyToAssign: agencyToAssign })
    }
}
